// ============================================================================
// datetime.js — DATA, ORA DHE ZONA KOHORE, sipas PAJISJES së përdoruesit.
//
// PROBLEMI I VJETËR: formati ishte i ngulitur ("sq-AL") kudo, prandaj një
// përdorues në Norvegji shihte format shqiptar; dhe vulat kohore shfaqeshin
// pa u konvertuar, kështu që ora e një veprimi të bërë në SHBA dukej ashtu siç
// ishte, jo në orën lokale të atij që po e lexonte.
//
// PARIMET:
//   1) RUAJTJA është gjithmonë e njëjta: një çast i vetëm, në UTC (timestamptz).
//      Zona kohore e shfaqjes NUK ruhet — ajo është e lexuesit, jo e shkruesit.
//   2) SHFAQJA bëhet me `Intl`, me:
//        · FORMATIN e rajonit të pajisjes (navigator.language, p.sh. "nb-NO"
//          → 10.08.2026; "en-GB" → 10/08/2026; "en-US" → 8/10/2026), dhe
//        · ZONËN KOHORE të pajisjes (Intl…resolvedOptions().timeZone).
//      Pra i njëjti çast duket ndryshe për secilin — siç duhet.
//   3) DATAT E KALENDARIT (datëlindja, data e vdekjes, "YYYY-MM-DD") NUK janë
//      çaste — ato s'kanë orë dhe s'guxojnë të zhvendosen nga zona kohore.
//      `new Date("1990-05-12")` e lexon si mesnatë UTC, prandaj në Amerikë do
//      të dilte 11 maj. Këtu trajtohen veçmas, si datë e pastër.
//
// KURTHI I TRETË: një vulë kohore pa zonë ("2026-08-10T09:12:33") vjen nga një
// kolonë `timestamp` (pa zonë). JavaScript-i e lexon si orë LOKALE, ndonëse në
// bazë është UTC. Prandaj `toDate()` i shton "Z" kur mungon zona.
// ============================================================================

// --- Rajoni dhe zona kohore e pajisjes -------------------------------------
// Gjuha e ndërfaqes dhe formati i datës janë dy gjëra të ndryshme: dikush mund
// ta lexojë aplikacionin shqip me telefon të konfiguruar për Norvegji — data
// duhet të dalë sipas Norvegjisë.
let localeOverride = null;

export function setDateLocale(tag) { localeOverride = tag || null; }

export function dateLocale() {
  if (localeOverride) return localeOverride;
  try {
    if (typeof navigator !== "undefined") {
      const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
      // Preferohet një etiketë me RAJON ("nb-NO"), sepse ajo e përcakton formatin.
      const withRegion = list.find((l) => l && /-[A-Za-z]{2}$/.test(l));
      return withRegion || list[0] || "en-GB";
    }
  } catch (e) { /* pa navigator → parazgjedhje */ }
  return "en-GB";
}

export function deviceTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch (e) { return "UTC"; }
}

// --- Leximi i vlerave nga baza ---------------------------------------------
const RE_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const RE_HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

// A është datë kalendarike e pastër ("1990-05-12")?
export function isDateOnly(v) {
  return typeof v === "string" && RE_DATE_ONLY.test(v.trim());
}

// Nga çfarëdo vlere → objekt Date, ose null. Vulat pa zonë trajtohen si UTC.
export function toDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "number") { const d = new Date(v); return isNaN(d.getTime()) ? null : d; }
  const s = String(v).trim();
  if (!s) return null;
  let iso = s;
  if (!RE_DATE_ONLY.test(s) && /^\d{4}-\d{2}-\d{2}[T ]/.test(s) && !RE_HAS_ZONE.test(s)) {
    iso = s.replace(" ", "T") + "Z"; // kolonë pa zonë → në bazë është UTC
  }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

// --- Formatuesit (të ruajtur në cache — Intl është i shtrenjtë) ------------
const cache = new Map();
function fmt(opts) {
  const loc = dateLocale();
  const key = loc + "|" + JSON.stringify(opts);
  if (cache.has(key)) return cache.get(key);
  let f;
  try { f = new Intl.DateTimeFormat(loc, opts); }
  catch (e) { try { f = new Intl.DateTimeFormat(undefined, opts); } catch (e2) { f = null; } }
  cache.set(key, f);
  return f;
}
// Kur ndryshon gjuha/rajoni (ose testohet), cache-ja pastrohet.
export function resetDateFormatters() { cache.clear(); }

const DASH = "—";

// --- DATA KALENDARIKE (pa orë, pa zhvendosje kohore) -----------------------
// Për datëlindje, datë vdekjeje, data varrimi — gjithçka që është "ditë", jo çast.
export function fmtDate(v, { fallback = DASH, style = "short" } = {}) {
  if (v == null || v === "") return fallback;
  const s = String(v).trim();
  if (isDateOnly(s)) {
    const [y, m, d] = s.split("-").map(Number);
    // Ndërtohet si mesnatë UTC dhe formatohet në UTC → dita s'lëviz kurrë.
    const dt = new Date(Date.UTC(y, m - 1, d));
    const f = fmt(style === "long"
      ? { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }
      : { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
    return f ? f.format(dt) : s;
  }
  const dt = toDate(s);
  if (!dt) return fallback;
  const f = fmt(style === "long"
    ? { year: "numeric", month: "long", day: "numeric", timeZone: deviceTimeZone() }
    : { year: "numeric", month: "2-digit", day: "2-digit", timeZone: deviceTimeZone() });
  return f ? f.format(dt) : dt.toISOString().slice(0, 10);
}

// --- ORA ËSHTË GJITHMONË 24-ORËSHE ----------------------------------------
//
// Kërkesë e userit, 1 shtator 2026: «orën nuk e dua askund me PM/AM, e dua 24h».
//
// Formati i DATËS mbetet i rajonit të pajisjes, siç e përshkruan koka e këtij
// skedari — ai parim nuk ndryshon. Por ora është rast më vete: 24-orëshi është
// standardi në Shqipëri, Kosovë dhe në pjesën më të madhe të Evropës, dhe
// «09:13 PM» te një regjistër hyrjesh lexohet më ngadalë se «21:13» edhe nga
// ata që janë mësuar me të.
//
// `hourCycle: "h23"` dhe jo `hour12: false`: e dyta, në disa rajone, prodhon
// «24:00» për mesnatën. `h23` jep gjithmonë 00–23.
const ORA24 = { hourCycle: "h23" };

// --- ÇAST NË KOHË (datë + orë), gjithmonë në zonën e lexuesit --------------
export function fmtDateTime(v, { fallback = DASH, seconds = false } = {}) {
  const dt = toDate(v);
  if (!dt) return fallback;
  const f = fmt({
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}),
    timeZone: deviceTimeZone(), ...ORA24,
  });
  return f ? f.format(dt) : dt.toISOString();
}

export function fmtTime(v, { fallback = DASH } = {}) {
  const dt = toDate(v);
  if (!dt) return fallback;
  const f = fmt({ hour: "2-digit", minute: "2-digit", timeZone: deviceTimeZone(), ...ORA24 });
  return f ? f.format(dt) : "";
}

// Për `title=`: data e plotë me emrin e zonës, që të mos ketë asnjë dykuptimësi
// ("10.08.2026, 11:12 (Ora e Evropës Qendrore)").
export function fmtDateTimeFull(v, { fallback = DASH } = {}) {
  const dt = toDate(v);
  if (!dt) return fallback;
  const f = fmt({
    dateStyle: "full", timeStyle: "long", timeZone: deviceTimeZone(), ...ORA24,
  });
  return f ? f.format(dt) : dt.toISOString();
}

// --- KOHË RELATIVE ("3 orë më parë") ---------------------------------------
// Përdor `Intl.RelativeTimeFormat` → vetë shfletuesi e jep në gjuhën e duhur,
// pa asnjë çelës përkthimi. Për më shumë se një muaj kthehet data e plotë.
const relCache = new Map();
function rel() {
  const loc = dateLocale();
  if (relCache.has(loc)) return relCache.get(loc);
  let f = null;
  try { f = new Intl.RelativeTimeFormat(loc, { numeric: "auto" }); }
  catch (e) { /* shfletues i vjetër */ }
  relCache.set(loc, f);
  return f;
}

export function fmtRelative(v, { fallback = DASH } = {}) {
  const dt = toDate(v);
  if (!dt) return fallback;
  const diffMs = dt.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const r = rel();
  if (!r) return fmtDateTime(v, { fallback });
  const MIN = 60000, HOUR = 3600000, DAY = 86400000;
  if (abs < MIN) return r.format(0, "second") || fmtDateTime(v);
  if (abs < HOUR) return r.format(Math.round(diffMs / MIN), "minute");
  if (abs < DAY) return r.format(Math.round(diffMs / HOUR), "hour");
  if (abs < 30 * DAY) return r.format(Math.round(diffMs / DAY), "day");
  return fmtDate(v, { fallback });
}

// --- Ndihmëse për fusha dhe emra skedarësh ---------------------------------
// `<input type="date">` kërkon GJITHMONË "YYYY-MM-DD" — ky format s'ka të bëjë
// me rajonin; shfletuesi ia shfaq përdoruesit sipas rajonit vetë.
export function toInputDate(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (isDateOnly(s)) return s;
  const dt = toDate(s);
  if (!dt) return "";
  const f = fmt({ year: "numeric", month: "2-digit", day: "2-digit", timeZone: deviceTimeZone() });
  if (!f) return dt.toISOString().slice(0, 10);
  const p = {};
  for (const x of f.formatToParts(dt)) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

// Data e sotme e pajisjes si "YYYY-MM-DD" (jo UTC — dita e përdoruesit).
export function todayISO() {
  const f = fmt({ year: "numeric", month: "2-digit", day: "2-digit", timeZone: deviceTimeZone() });
  const now = new Date();
  if (!f) return now.toISOString().slice(0, 10);
  const p = {};
  for (const x of f.formatToParts(now)) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

// Për emra skedarësh: stabil, i renditshëm, i pavarur nga rajoni.
export function stampForFilename() { return todayISO(); }

// ============================================================================
// SHKRIMI I LIRË I DATËS — "29061984" → 29.06.1984
//
// Njerëzit e shkruajnë datën si u vjen: me pika, me vija, me tërthore ose fare
// pa ndarje. Dhe RENDITJA ndryshon sipas vendit: 29.06.1984 në Evropë,
// 06/29/1984 në SHBA, 1984-06-29 në Japoni/ISO. Prandaj renditja NUK ngulitet —
// merret nga vetë rajoni i pajisjes, njësoj si formati i shfaqjes.
// ============================================================================

// Renditja e pjesëve sipas rajonit: "dmy" | "mdy" | "ymd".
export function dateOrder() {
  const f = fmt({ year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
  if (!f) return "dmy";
  try {
    const seq = f.formatToParts(new Date(Date.UTC(2000, 0, 2)))
      .filter((p) => p.type === "day" || p.type === "month" || p.type === "year")
      .map((p) => p.type[0]) // d | m | y
      .join("");
    if (seq === "mdy" || seq === "ymd" || seq === "dmy") return seq;
  } catch (e) { /* biem te parazgjedhja */ }
  return "dmy";
}

// Shenja ndarëse që përdor rajoni (".", "/", "-").
export function dateSeparator() {
  const f = fmt({ year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
  if (!f) return ".";
  try {
    const lit = f.formatToParts(new Date(Date.UTC(2000, 0, 2))).find((p) => p.type === "literal");
    const ch = lit && lit.value && lit.value.trim();
    if (ch && /^[./-]$/.test(ch)) return ch;
  } catch (e) { /* parazgjedhje */ }
  return ".";
}

// Shembulli që i tregohet përdoruesit te fusha ("dd.mm.yyyy").
export function datePlaceholder() {
  const sep = dateSeparator();
  const o = dateOrder();
  if (o === "ymd") return ["yyyy", "mm", "dd"].join(sep);
  if (o === "mdy") return ["mm", "dd", "yyyy"].join(sep);
  return ["dd", "mm", "yyyy"].join(sep);
}

function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

// Viti dyshifror: 84 → 1984, 05 → 2005. Pika e kthesës është viti i sotëm.
function expandYear(yy) {
  const now = new Date().getUTCFullYear();
  const c = 2000 + yy;
  return c > now ? 1900 + yy : c;
}

// Nga çfarëdo shkrimi → "YYYY-MM-DD", ose null nëse s'kuptohet.
// Pranon: 29061984 · 29.06.1984 · 29/06/1984 · 29-6-84 · 1984-06-29 · 1984
export function parseLooseDate(input, { order } = {}) {
  const raw = String(input == null ? "" : input).trim();
  if (!raw) return null;
  const ord = order || dateOrder();
  const toks = raw.split(/[^0-9]+/).filter(Boolean);
  const digits = toks.join("");
  if (!digits) return null;

  let y = null, m = null, d = null;

  if (toks.length === 3) {
    // Ndarësit e tregojnë vetë ku është viti: pjesa me 4 shifra.
    if (toks[0].length === 4) { y = +toks[0]; m = +toks[1]; d = +toks[2]; }
    else if (toks[2].length === 4) {
      y = +toks[2];
      if (ord === "mdy") { m = +toks[0]; d = +toks[1]; } else { d = +toks[0]; m = +toks[1]; }
    } else {
      // Të tria të shkurtra — vendos renditja e rajonit.
      if (ord === "ymd") { y = expandYear(+toks[0]); m = +toks[1]; d = +toks[2]; }
      else if (ord === "mdy") { m = +toks[0]; d = +toks[1]; y = expandYear(+toks[2]); }
      else { d = +toks[0]; m = +toks[1]; y = expandYear(+toks[2]); }
    }
  } else if (digits.length === 8) {
    if (ord === "ymd") { y = +digits.slice(0, 4); m = +digits.slice(4, 6); d = +digits.slice(6, 8); }
    else if (ord === "mdy") { m = +digits.slice(0, 2); d = +digits.slice(2, 4); y = +digits.slice(4, 8); }
    else { d = +digits.slice(0, 2); m = +digits.slice(2, 4); y = +digits.slice(4, 8); }
  } else if (digits.length === 6) {
    if (ord === "ymd") { y = expandYear(+digits.slice(0, 2)); m = +digits.slice(2, 4); d = +digits.slice(4, 6); }
    else if (ord === "mdy") { m = +digits.slice(0, 2); d = +digits.slice(2, 4); y = expandYear(+digits.slice(4, 6)); }
    else { d = +digits.slice(0, 2); m = +digits.slice(2, 4); y = expandYear(+digits.slice(4, 6)); }
  } else if (digits.length === 4 && toks.length === 1) {
    // Vetëm viti — si më parë, plotësohet me 1 janar.
    y = +digits; m = 1; d = 1;
  } else {
    return null;
  }

  // "Çfarë deshi të thoshte": nëse muaji del >12 por dita ≤12, ai i ka
  // ndërruar vendet pa dashje (ose ka shkruar sipas një rajoni tjetër).
  if (m > 12 && d <= 12) { const x = m; m = d; d = x; }

  if (!y || !m || !d) return null;
  if (y < 1000 || y > 9999) return null;
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > daysInMonth(y, m)) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Maska gjatë shkrimit: shifrat e shkruara → "29.06.1984" pa e ndalur njeriun.
// Ndarësit që shkruan vetë injorohen — llogariten vetëm shifrat.
export function maskDateInput(text, { order } = {}) {
  const ord = order || dateOrder();
  const sep = dateSeparator();
  const dg = String(text == null ? "" : text).replace(/[^0-9]/g, "").slice(0, 8);
  if (!dg) return "";
  const cuts = ord === "ymd" ? [4, 6] : [2, 4];
  let out = "";
  for (let i = 0; i < dg.length; i++) {
    if (cuts.includes(i)) out += sep;
    out += dg[i];
  }
  return out;
}
